import {Component, OnInit, ChangeDetectionStrategy, OnDestroy, Input, ViewChild} from '@angular/core';
import {FormControl, Validators, FormGroup} from '@angular/forms';
import {
    FormInputType,
    LoginData,
    FormDataService,
    InfoBannerType,
    UtilsService,
    UserService,
    FriendlyCaptchaService, LoginStatus, FriendlyCaptchaStatus, CaptchaActionNames
} from '../../../../shared';
import {tap, takeUntil, mergeMap, catchError} from 'rxjs/operators';
import {EMPTY, Observable, Subject, throwError} from 'rxjs';
import {FriendlyCaptchaWidgetComponent} from "../../../../registration";

@Component({
    selector: 'sl-anonymous-login-form',
    templateUrl: './anonymous-login-form.component.html',
    styleUrls: []
})
export class AnonymousLoginFormComponent implements OnInit, OnDestroy {
    protected readonly CaptchaActionNames = CaptchaActionNames;

    @ViewChild(FriendlyCaptchaWidgetComponent) captchaWidget: FriendlyCaptchaWidgetComponent;
    @Input() redirectRoute: string;

    public InputType = FormInputType;
    public emailForm: FormGroup;
    public bannerTextKey: string;
    public bannerType: InfoBannerType;
    public showBanner: boolean;
    public requestGuestLoginAction: Observable<unknown>;
    public friendlyCaptchaStatus: FriendlyCaptchaStatus;

    private unsubscribe$ = new Subject<any>();

    constructor(private utilsService: UtilsService,
                private userService: UserService,
                public friendlyCaptchaService: FriendlyCaptchaService,
                private formDataService: FormDataService) {
    }

    ngOnInit(): void {
        this.createForm();
        this.guestLogin();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public submit() {
        if (!this.emailForm.valid) {
            this.formDataService.markControlsTouched(this.emailForm);
            return;
        }
    }

    public setFriendlyCaptcha(event: FriendlyCaptchaStatus) {
        this.friendlyCaptchaStatus = event;
    }

    private guestLogin() {
        this.requestGuestLoginAction = this.utilsService.createActionObs(() => {
            return this.friendlyCaptchaService.getToken().pipe(
                mergeMap(response =>
                    this.friendlyCaptchaService.checkCaptchaResponse(response, this.friendlyCaptchaStatus, CaptchaActionNames.LOGIN)
                ),
                mergeMap(response =>
                    this.userService.loginAndUpdateStoredData(
                        AnonymousLoginFormComponent.getLoginObj(this.emailForm.get('email').value, response), true
                    )
                ),
                this.makeLoginResponse(),
                takeUntil(this.unsubscribe$)
            );
        });
    }

    private createForm() {
        this.emailForm = new FormGroup(AnonymousLoginFormComponent.getEmailFormControl());
    }

    private static getEmailFormControl() {
        return {
            email: new FormControl(null, {
                updateOn: 'change',
                validators: [Validators.required, Validators.email]
            })
        };
    }

    private makeLoginResponse() {
        return tap(
            () => {
                this.userService.restoreSavedRoute(this.redirectRoute);
            },
            (err) => {
                this.friendlyCaptchaService.setToken(null);
                this.setLoginError(err);
                this.resetWidget();
            }
        );
    }

    private static getLoginObj(email: string, response: string): LoginData {
        return {
            username: email,
            friendlyCaptchaResponse: response
        };
    }

    private setLoginError(err: any) {
        this.setBannerState();
        if (AnonymousLoginFormComponent.isEmailAlreadyInUse(err)) {
            this.bannerTextKey = 'ERRORS.ERROR_USER_EXISTS.text', 'ERRORS.ERROR_USER_EXISTS.title';
        } else if (err.error && err.error.errors[0].code === 'ERROR_CAPTCHA_UNCHECKED') {
            this.bannerTextKey = 'ERRORS.ERROR_CAPTCHA_UNCHECKED.text';
        } else {
            this.bannerTextKey = 'GENERAL.VALIDATION_ERRORS.guestLoginFailed';
        }
    }

    private static isEmailAlreadyInUse(err: any): boolean {
        return err.error && err.error.errors[0].code === 'ERROR_USER_EXISTS';
    }

    private setBannerState(): void {
        this.bannerType = InfoBannerType.DANGER;
        this.showBanner = true;
    }

    private resetWidget() {
        this.captchaWidget?.resetWidget();
    }
}
